import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography } from '@mui/material';
import { fetchWatchlistRevision } from '../utils/ApiCall';
import { formatUserFactorModel } from '../utils/factorDataOptions';
import { factorVendorSwitchedNotice } from '../context/factorDataSyncPolicy';

// The durable removal-review phase is shared by every app route. Fetch only
// that exact revision for display; navigation never approves or restages it.
export default function FactorContextReviewNotice({
    status, publicationState, sessionScopeToken, suppressed = false, onReview,
}) {
    const revisionId = status?.found !== false && status?.topLevelStatus === 'running' && status?.phase === 'removal_review'
        ? status.revisionId : null;
    const [revision, setRevision] = useState(null);
    const [dismissedRevision, setDismissedRevision] = useState(null);
    const [notice, setNotice] = useState('');
    const completedRevision = useRef(null);

    useEffect(() => {
        if (!revisionId || !sessionScopeToken) return undefined;
        let active = true;
        const load = async () => {
            try {
                const result = await fetchWatchlistRevision(revisionId);
                if (active && result?.id === revisionId) setRevision(result);
            } catch (_error) {
                // The durable phase already establishes that review is needed.
                // History owns loading/retrying the full review.
            }
        };
        void load();
        return () => { active = false; };
    }, [revisionId, sessionScopeToken]);

    useEffect(() => {
        if (!revision?.id || completedRevision.current === revision.id) return;
        const vendor = revision.source_metadata_json?.factor_vendor_switch;
        if (publicationState?.revisionId === revision.id && publicationState.committed) {
            completedRevision.current = revision.id;
            setNotice(vendor?.to?.vendor
                ? factorVendorSwitchedNotice(vendor.to.vendor)
                : 'Factor data updated. Your Watchlist and risk data are ready.');
        } else if (status?.revisionId === revision.id && status.cancelled) {
            completedRevision.current = revision.id;
            setNotice(vendor?.from?.vendor
                ? `Switch cancelled. Still using ${formatUserFactorModel(vendor.from.vendor)}.`
                : 'Update cancelled. Your current Watchlist and factor data remain active.');
        }
    }, [publicationState, revision, status]);

    const metadata = revision?.id === revisionId ? revision.source_metadata_json : null;
    const vendor = metadata?.factor_vendor_switch;
    const target = vendor?.to?.vendor ? formatUserFactorModel(vendor.to.vendor) : '';
    const current = vendor?.from?.vendor ? formatUserFactorModel(vendor.from.vendor) : '';
    const review = metadata?.context_refresh_removal_review;
    const changeCount = review ? [
        'watchlist_securities', 'removed_listing_securities', 'added_securities', 'country_changed_securities',
    ].reduce((total, field) => total + (review[field]?.length || 0), 0) : null;
    const title = target ? `Your ${target} switch needs review` : 'Your Watchlist update needs review';
    const open = Boolean(revisionId && sessionScopeToken && dismissedRevision !== revisionId && !suppressed);
    const openReview = async () => {
        const navigated = await onReview({
            origin: 'watchlist_context_refresh',
            revisionId,
            status: 'changes_awaiting_review',
            sessionScopeToken,
        });
        if (navigated !== false) setDismissedRevision(revisionId);
    };

    return (
        <>
            <Dialog open={open} onClose={() => setDismissedRevision(revisionId)}
                aria-labelledby="factor-review-title" maxWidth="sm" fullWidth>
                <DialogTitle id="factor-review-title">{title}</DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        {changeCount != null
                            ? `${target ? `Switching to ${target}` : 'Updating factor data'} requires ${changeCount} Watchlist ${changeCount === 1 ? 'change' : 'changes'}.`
                            : 'The new data requires changes to your Watchlist.'}
                        {' Review the changes before completing the update.'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {current ? `${current} remains active until you approve the switch.`
                            : 'Your current Watchlist and factor data remain active until you approve.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDismissedRevision(revisionId)}>Later</Button>
                    <Button variant="contained" onClick={openReview}>Review Watchlist changes</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={Boolean(revisionId && !open && !suppressed && sessionScopeToken)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity="info" variant="filled"
                    action={<Button color="inherit" onClick={openReview}>Review changes</Button>}>
                    {title}
                </Alert>
            </Snackbar>
            <Snackbar open={Boolean(notice)} autoHideDuration={7000} onClose={() => setNotice('')}>
                <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>
            </Snackbar>
        </>
    );
}
